<script setup>
import { ref, computed } from 'vue';
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
  deals: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'move-deal',
  'edit-deal',
  'click-deal',
  'new-deal-in-stage',
  'edit-stage',
]);

const isDragOver = ref(false);

const stageDeals = computed(() => {
  return props.deals
    .filter(deal => Number(deal.pipeline_stage_id) === Number(props.stage.id))
    .sort((a, b) => (a.position || 0) - (b.position || 0));
});

const totalValue = computed(() => {
  const sum = stageDeals.value.reduce((acc, deal) => acc + (Number(deal.value) || 0), 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(sum);
});

const onDragOver = event => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
  isDragOver.value = true;
};

const onDragLeave = () => {
  isDragOver.value = false;
};

const onDrop = event => {
  event.preventDefault();
  isDragOver.value = false;
  try {
    const data = JSON.parse(event.dataTransfer.getData('text/plain'));
    if (data && data.dealId) {
      emit('move-deal', {
        dealId: data.dealId,
        fromStageId: data.fromStageId,
        toStage: props.stage,
      });
    }
  } catch (err) {
    console.error('Failed to parse drag drop data:', err);
  }
};
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 w-72 bg-n-alpha-1 dark:bg-n-solid-1/80 rounded-2xl border border-n-weak h-full max-h-full transition-all shadow-xs overflow-hidden"
    :class="{ 'ring-2 ring-brand border-brand': isDragOver }"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <!-- Modern Stage Header -->
    <div class="px-3.5 py-2.5 flex items-center justify-between border-b border-n-weak bg-n-surface-1">
      <div class="flex items-center gap-2 truncate">
        <!-- Stage Color Pill -->
        <span
          class="w-2.5 h-2.5 rounded-full shrink-0"
          :style="{ backgroundColor: stage.color || '#3B82F6' }"
        />
        <h3 class="font-semibold text-xs text-n-slate-12 tracking-tight truncate">
          {{ stage.name }}
        </h3>
        <span class="text-[10px] min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-11 font-semibold font-mono">
          {{ stageDeals.length }}
        </span>
      </div>

      <div class="flex items-center gap-0.5">
        <button
          type="button"
          class="p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors cursor-pointer"
          title="Configurar etapa"
          @click="emit('edit-stage', stage)"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>

        <button
          type="button"
          class="p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors cursor-pointer"
          title="Nova oportunidade nesta etapa"
          @click="emit('new-deal-in-stage', stage)"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Quick Add Button -->
    <div class="p-2 border-b border-n-weak bg-n-alpha-1">
      <button
        type="button"
        class="w-full py-1.5 px-3 rounded-lg border border-dashed border-n-weak hover:border-brand bg-n-surface-1 hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 text-xs font-medium transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
        @click="emit('new-deal-in-stage', stage)"
      >
        <svg class="w-3.5 h-3.5 text-n-slate-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        <span>Adicionar oportunidade</span>
      </button>
    </div>

    <!-- Cards Scroll Area -->
    <div class="flex-1 p-2 overflow-y-auto space-y-2 min-h-[140px]">
      <KanbanCard
        v-for="deal in stageDeals"
        :key="deal.id"
        :deal="deal"
        @edit="emit('edit-deal', $event)"
        @click-deal="emit('click-deal', $event)"
      />

      <div
        v-if="stageDeals.length === 0"
        class="h-28 flex flex-col items-center justify-center border border-dashed border-n-weak rounded-xl text-n-slate-10 text-xs text-center p-3"
      >
        <span class="text-[11px] font-medium text-n-slate-10">Arraste ou crie negócios aqui</span>
      </div>
    </div>

    <!-- Column Footer with Stage Total -->
    <div class="px-3.5 py-1.5 bg-n-surface-1 border-t border-n-weak flex items-center justify-between text-[11px] text-n-slate-11">
      <span>Total:</span>
      <span class="font-semibold text-n-slate-12 font-mono">{{ totalValue }}</span>
    </div>
  </div>
</template>
